import { InternalNode, Author, AuthorId, NodeId } from './entities/article';
import { ListenableSubscribable } from '@benzinga/subscribable';

const TWELVE_HOURS_MS = 432 * 100000;

interface AuthorEvent {
  type: 'article:add_author' | 'article:created_authors' | 'article:updated_authors' | 'article:removed_author';
  author: Author;
}

interface ArticleEvent {
  type: 'article:updated_articles' | 'article:created_articles';
  article: InternalNode;
}

interface AuthorsUpdateEvent {
  type: 'article:updated_authors';
  authors: Author[];
}

export type ArticleStoreEvent = ArticleEvent | AuthorEvent | AuthorsUpdateEvent;

export class ArticleStore extends ListenableSubscribable<ArticleStoreEvent> {
  private date?: Date;
  private authors?: Author[];
  private articles?: InternalNode[];

  constructor() {
    super();
    this.date = undefined;
    this.authors = undefined;
    this.articles = undefined;
  }

  public shouldFetchAuthorByPath = (authorSlug: string): boolean => {
    const lastCalled = this.date?.getTime() ?? 0;
    if (this.authors === undefined || !this.getAuthorByPath(authorSlug) || Date.now() - lastCalled > TWELVE_HOURS_MS) {
      return true;
    }
    return false;
  };

  public shouldFetchAuthor = (authorId: AuthorId): boolean => {
    const lastCalled = this.date?.getTime() ?? 0;
    if (this.authors === undefined || !this.getAuthorById(authorId) || Date.now() - lastCalled > TWELVE_HOURS_MS) {
      return true;
    }
    return false;
  };

  public shouldFetchArticle = (nodeId: NodeId): boolean => {
    const lastCalled = this.date?.getTime() ?? 0;
    if (this.articles === undefined || !this.getArticleById(nodeId) || Date.now() - lastCalled > TWELVE_HOURS_MS) {
      return true;
    }
    return false;
  };

  public getAuthors = (): Author[] | undefined => {
    return this.authors;
  };

  public getArticles = (): InternalNode[] | undefined => {
    return this.articles;
  };

  public getAuthorByPath = (path: string): Author | undefined => {
    return this.authors?.find(a => a.profileUrlSlug === path);
  };

  public getAuthorById = (authorId: AuthorId): Author | undefined => {
    return this.authors?.find(a => a.uid === authorId);
  };

  public getArticleById = (articleId: NodeId): InternalNode | undefined => {
    return this.articles?.find(a => a.NodeID === articleId);
  };

  public addAuthor = (author: Author): void => {
    this.date = new Date();
    const authorIndex = this.authors?.findIndex(a => a.uid === author.uid);

    if (authorIndex !== undefined && this.authors && authorIndex >= 0) {
      this.authors[authorIndex] = author;
    } else {
      if (this.authors) {
        this.authors.push(author);
      } else {
        this.authors = [author];
      }
    }

    this.dispatch({
      author: author,
      type: 'article:created_authors',
    });
    this.dispatch({
      authors: this.getAuthorsForce(),
      type: 'article:updated_authors',
    });
  };

  public addArticle = (article: InternalNode): void => {
    this.date = new Date();
    const articleIndex = this.articles?.findIndex(a => a.NodeID === article.NodeID);

    if (articleIndex !== undefined && this.articles && articleIndex >= 0) {
      this.articles[articleIndex] = article;
    } else {
      if (this.articles) {
        this.articles.push(article);
      } else {
        this.articles = [article];
      }
    }

    this.dispatch({
      article: article,
      type: 'article:created_articles',
    });
  };

  public getAuthorsForce = (): Author[] => {
    return this.authors ? [...this.authors] : [];
  };

  public getArticlesForce = (): InternalNode[] => {
    return this.articles ? [...this.articles] : [];
  };
}
